import React, { useEffect, useRef, useState } from "react";
import { GlobalSearchSectionsType } from "../types/algolia";
import { analyticsViewFilterEvent } from "@/services/algolia/analytics";

const useFilterViewAnalytics = <T extends {} | unknown>({
  items,
  containerRef,
  analyticsPayload,
  filterStringCb,
}: {
  items: Array<T>;
  containerRef: React.RefObject<HTMLElement>;
  analyticsPayload: { kind: GlobalSearchSectionsType; index: string; userToken?: string };
  filterStringCb: (index: number) => string;
}): { itemRefs: React.MutableRefObject<(HTMLElement | null)[]> } => {
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const [newVisibleItems, setNewVisibleItems] = useState<number[]>([]);

  const seenItems = useRef<Set<number>>(new Set());
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const newVisibleQueue = useRef<Set<number>>(new Set());
  const [isContainerVisible, setContainerVisibility] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const containerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setContainerVisibility(entry.isIntersecting);
        });
      },
      {
        root: null,
        threshold: 0.1,
      }
    );

    containerObserver.observe(containerRef.current);

    return () => {
      containerObserver.disconnect();
    };
  }, [containerRef.current]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !items.length || !isContainerVisible) return; // return if the container is not available or items are an empty array
    //initialize the intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        let hasNew = false; //for tracking if new items have been added to the viewport
        entries.forEach((entry) => {
          const index = Number(entry.target.getAttribute("data-index")); //get the index of the item

          //if the item is in the viewport and has not been seen before
          if (entry.isIntersecting && !seenItems.current.has(index)) {
            newVisibleQueue.current.add(index);
            hasNew = true;
          }
        });
        if (hasNew) {
          // clear any previous timers if exists
          if (debounceTimer.current) clearTimeout(debounceTimer.current);

          debounceTimer.current = setTimeout(() => {
            const newItems = Array.from(newVisibleQueue.current); //convert to array from Set
            newVisibleQueue.current.clear();

            newItems.forEach((index) => seenItems.current.add(index));

            setVisibleItems((prev) => Array.from(new Set([...prev, ...newItems])));

            setNewVisibleItems(newItems);
          }, 1000); // 1 second debounce
        }
      },
      {
        root: container,
        threshold: 0.5, //if 50% visible
      }
    );
    // observe each item in the list
    itemRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [items, containerRef.current, isContainerVisible]);

  // To observe if the accordian panel is open
  const [isPanelOpen, setPanelOpen] = useState<boolean>(false);
  useEffect(() => {
    const htmlEl = containerRef.current;
    if (!htmlEl) return;
    if (!htmlEl.getAttribute("tabindex")) setPanelOpen(true); // if tabindex is not set, set panelOpen to true
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.attributeName === "tabindex") {
          const tabIndex = htmlEl.getAttribute("tabindex");
          setPanelOpen(tabIndex === "-1" ? false : true);
        }
      });
    });

    observer.observe(htmlEl, { attributes: true, attributeFilter: ["tabindex"] });

    return () => {
      observer.disconnect();
    };
  }, [containerRef.current]);

  useEffect(() => {
    if (!newVisibleItems.length || !isPanelOpen) return;

    const chunkSize = 10;
    let seenFilters = newVisibleItems?.map(filterStringCb);

    const chunkedCategories = [];
    for (let i = 0; i < seenFilters.length; i += chunkSize) {
      chunkedCategories.push(seenFilters.slice(i, i + chunkSize));
    }

    // Send analytics events for each chunk
    chunkedCategories.forEach((chunk, index) => {
      // You can add a delay or check if the previous event has been completed before sending the next
      analyticsViewFilterEvent({ ...analyticsPayload, filters: chunk });
    });
  }, [visibleItems, isPanelOpen]);

  return { itemRefs };
};

export default useFilterViewAnalytics;
